import {
  useSelection,
  useSelectionActions
} from '@cube3/state/src/redux/components/useSelection';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { useDragPermissions } from './useDragPermissions';
import { createGhostImageContainer, createGhostImage } from './elements';
import { useHistory, useLocation } from 'react-router';
import { useDropHandler } from './useDropHandler';
import { useVisitingPathParams } from '../../hooks/useVisitingPathParams';
import { getDropTargetResource } from './utils/getDropTargetFolder';
import { filterDragableSelections } from './utils/filterDragableSelection';
import { ResourceIdentifier } from '@cube3/state/types';
import { useVisitingFolder } from '@cube3/state/src/redux/components/Hooks/clipboard/useVisitingFolder';

export type DropTarget = {
  id: string;
  type: 'folder' | 'project' | 'navigation' | 'content-tree';
  pathname?: string;
};

export type DraggableItem = {
  type: 'asset' | 'folder';
  id: string;
  /** The parent folder id of the dragged items */
  parent?: string;
};

export const DND_DELAY = 350;

/** container for ghost image */
const container = createGhostImageContainer();
let draggedItems: DraggableItem[] = undefined;
let draggedImages = [];

export const DragAndDropContext = React.createContext<DropTarget>(undefined);

export const Draggable = ({ children }) => {
  const { selection } = useSelection();

  const selectedItems = useMemo(() => {
    return filterDragableSelections(selection);
  }, [selection]);

  const { addItems, clearItems, clearActiveItem } = useSelectionActions();

  const [sourceFolder, setSourceFolder] =
    useState<ResourceIdentifier<'folder'>>(null);

  const [folderId] = useVisitingFolder({});

  const [dropTarget, setDropTarget] = useState<DropTarget>(undefined);
  const dropTargetEl = useRef<HTMLElement>(null);

  const { allowDrag, allowDrop } = useDragPermissions();
  const { inLibraryRoot } = useVisitingPathParams();

  const handleDrop = useDropHandler();

  /** reset state */
  const clear = useCallback(() => {
    setDropTarget(undefined);
    dropTargetEl.current = null;
    draggedItems = undefined;
    draggedImages = [];
    clearActiveItem();
    clearItems();
  }, [clearActiveItem, clearItems]);

  const onDragStart = useCallback(
    (e) => {
      setSourceFolder({ type: 'folder', id: folderId });
      setDropTarget(null);
      if (!allowDrag) return;

      /** get element containing dragable data  */
      const composed = e.composedPath();
      const draggedEl = composed.find((el) =>
        ['asset', 'folder'].includes(getDropTargetResource(el).type)
      );

      if (draggedEl) {
        const { id, type } = getDropTargetResource(draggedEl);
        const { src, parent } = draggedEl.dataset;
        const item = { id, type, parent } as DraggableItem;

        let alreadySelected;

        if (!selectedItems) {
          draggedItems = [item];
          draggedImages = [src];
        } else {
          const { data, images } = selectedItems;
          alreadySelected = !!data.find((s) => s.id === id);
          draggedItems = alreadySelected ? data : [...data, item];
          draggedImages = alreadySelected ? images : [...images, src];
        }

        if (!alreadySelected) {
          addItems(item);
        }

        try {
          createGhostImage(container, draggedImages);
          e.dataTransfer.setDragImage(container, 0, 0);
        } catch (error) {
          console.error(error);
        }
      }
    },
    [addItems, allowDrag, selectedItems, folderId]
  );

  console.info('dnd - draggable', { dropTarget, draggedItems });

  /** prevent accidental upload of undone dragged items */
  const onDragEnd = useCallback(() => {
    draggedItems = undefined;
    draggedImages = [];
  }, []);

  //  occurs when the dragged element enters or is over the drop target
  const onDragOver = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!draggedItems) return;

      const composed = e.composedPath();
      const item = composed.find(getDropTargetResource);
      const { id: dropId, type: dropType } = getDropTargetResource(item) || {};

      if (
        item &&
        !draggedItems.some((i) => i.id === dropId && i.type === dropType)
      ) {
        setDropTarget({
          id: dropId,
          type: item.dataset.type,
          pathname: item.dataset.pathname
        });
        dropTargetEl.current = item;
      }
    },
    [dropTarget]
  );

  const onDragLeave = useCallback(() => {
    // prevent snackbar item from flashing
    if (!allowDrop) return;
    if (dropTarget) {
      setDropTarget(undefined);
      dropTargetEl.current = null;
    }
  }, [allowDrop, dropTarget]);

  const history = useHistory();
  const { pathname } = useLocation();
  // Trigger navigation when dragging items to breadcrumbs or some navItems
  useEffect(() => {
    if (
      draggedItems &&
      dropTarget?.pathname &&
      pathname !== dropTarget.pathname
    ) {
      if (dropTarget.pathname) {
        const timer = setTimeout(() => {
          history.push(dropTarget.pathname, { from: pathname });
        }, DND_DELAY);
        return () => clearTimeout(timer);
      }
    }
  }, [dropTarget, pathname]);

  // occurs when the dragged element is dropped on the drop target
  const onDrop = useCallback(() => {
    if (allowDrop && draggedItems?.length && dropTarget && !inLibraryRoot) {
      handleDrop(draggedItems, dropTarget, sourceFolder);
    }
    clear();
  }, [
    inLibraryRoot,
    draggedItems,
    allowDrop,
    dropTarget,
    clear,
    handleDrop,
    sourceFolder
  ]);

  useEffect(() => {
    if (allowDrag) {
      window.addEventListener('dragstart', onDragStart, true);
      window.addEventListener('dragend', onDragEnd, true);
      window.addEventListener('dragover', onDragOver, true);
    }
    if (dropTargetEl.current) {
      dropTargetEl.current.addEventListener('dragleave', onDragLeave, true);
    }

    window.addEventListener('drop', onDrop, true);

    return () => {
      if (allowDrag) {
        window.removeEventListener('dragstart', onDragStart, true);
        window.removeEventListener('dragend', onDragEnd, true);
        window.removeEventListener('dragleave', onDragLeave, true);
      }
      if (dropTargetEl.current) {
        dropTargetEl.current.removeEventListener('dragover', onDragOver, true);
      }
      window.removeEventListener('drop', onDrop, true);
    };
  }, [allowDrag, onDragEnd, onDragLeave, onDragStart, onDragOver, onDrop]);

  return <DragAndDropContext.Provider value={dropTarget} children={children} />;
};
